import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faCircleHalfStroke, faMoon, faSun } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../context/ThemeContext";
import { Theme } from "../../utils/theme";
import styles from "./header.module.scss";

const THEME_OPTIONS: ReadonlyArray<{ value: Theme; icon: IconDefinition }> = [
    { value: "light", icon: faSun },
    { value: "dark", icon: faMoon },
    { value: "system", icon: faCircleHalfStroke },
];

function iconForTheme(theme: Theme): IconDefinition {
    return THEME_OPTIONS.find((option) => option.value === theme)?.icon ?? faCircleHalfStroke;
}

// The Light/Dark/System radiogroup used inside the desktop theme popover.
export function ThemeOptionGroup({ className }: { className?: string }) {
    const { t } = useTranslation();
    const { theme, setTheme } = useTheme();

    return (
        <div className={className ?? styles.themeOptions} role="radiogroup" aria-label={t("themeSwitcher.title")}>
            {THEME_OPTIONS.map(({ value, icon }) => {
                const selected = theme === value;
                return (
                    <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={selected ? `${styles.themeOption} ${styles.themeOptionSelected}` : styles.themeOption}
                        onClick={() => setTheme(value)}
                    >
                        <FontAwesomeIcon icon={icon} aria-hidden="true" />
                        {t(`themeSwitcher.${value}`)}
                    </button>
                );
            })}
        </div>
    );
}

export default function ThemeSwitcher() {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const panelId = useId();
    const label = t(`themeSwitcher.${theme}`);

    useEffect(() => {
        if (!isOpen) return undefined;
        const dismiss = (event: MouseEvent | TouchEvent | KeyboardEvent) => {
            if (event instanceof KeyboardEvent) {
                if (event.key !== "Escape") return;
                setIsOpen(false);
                triggerRef.current?.focus();
                return;
            }
            const target = event.target as Node;
            if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) setIsOpen(false);
        };
        document.addEventListener("mousedown", dismiss);
        document.addEventListener("touchstart", dismiss, { passive: true });
        document.addEventListener("keydown", dismiss);
        return () => {
            document.removeEventListener("mousedown", dismiss);
            document.removeEventListener("touchstart", dismiss);
            document.removeEventListener("keydown", dismiss);
        };
    }, [isOpen]);

    return (
        <div className={styles.themeSwitcher}>
            <button
                type="button"
                ref={triggerRef}
                className={styles.themeBadge}
                onClick={() => setIsOpen((open) => !open)}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-controls={isOpen ? panelId : undefined}
                aria-label={t("themeSwitcher.open", { theme: label })}
            >
                <FontAwesomeIcon icon={iconForTheme(theme)} aria-hidden="true" />
            </button>
            {isOpen && (
                <div ref={panelRef} id={panelId} className={styles.themePanel} role="dialog" aria-label={t("themeSwitcher.title")}>
                    <strong>{t("themeSwitcher.title")}</strong>
                    <p>{t("themeSwitcher.hint")}</p>
                    <ThemeOptionGroup />
                </div>
            )}
        </div>
    );
}
